/**
 * LEAD360-DEDUP-V1 (V1-P3) -- Owner Lead detail route
 * (`/dashboard/owner/leads/:safeLeadId`) as a REDIRECT SHIM into the canonical.
 *
 * Route singularity (Epic 7 close): there is ONE canonical full-page lead
 * detail, `/dashboard/leads/:safeLeadId` (LeadsWorkspaceDetailPage), rendering
 * the ONE canonical component (Lead360CanonicalView). The owner is in the Leads
 * Workspace audience, so me.leadsWorkspaceEnabled is present and this route
 * REDIRECTS into the canonical (preserving the clinicId narrowing) so the owner
 * never renders an alternative 360.
 *
 * The internal-surface env flag was retired by RECEPTIONIST-EXPERIENCE-STITCH-V1;
 * me.leadsWorkspaceEnabled is now a flag-free AUDIENCE signal (always present
 * for the owner). The legacy owner-detail body below is a vestigial fallback for
 * the transient me-absent case only; it composes the SAME canonical
 * `LeadDetailContent`, so there is still one component, never two divergent
 * renders.
 *
 * The crossmatch notice + audited reveal + workbench wiring are unchanged in the
 * legacy path (mounted per FRONT-DESK-POSSIBLE-MATCH-SURFACE-01 / S2-P2C).
 */

import { type ReactNode, useEffect, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import {
  bookAppointment,
  setLeadBookingClinic,
  confirmAppointment,
  cancelAppointment,
  fetchOwnerDashboard,
  fetchOwnerLead360,
  markAppointmentArrived,
  markAppointmentCheckedIn,
  markAppointmentFulfilled,
  markAppointmentNoShow,
  markAppointmentNotConverted,
  rescheduleAppointment,
} from '@/dashboard/api';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { useDashboardMeContext } from '@/dashboard/me-context';
// THE ONE origin grammar (founder ruling 2026-08-08) -- see the sibling
// import in LeadsWorkspaceDetailPage; never a second attribution vocabulary.
import { resolveOriginChipLabel } from '@/dashboard/owner/Lead360Panel';
import LeadPatientCrossmatchNotice from '@/dashboard/patients/LeadPatientCrossmatchNotice';
import AppointmentWorkbench, {
  canUseAppointmentWorkbench,
  isBookConfirmOnlyRole,
} from '@/dashboard/owner/AppointmentWorkbench';
import { resolveCancelReasonCodes } from '@/dashboard/appointments/cancel-reasons';
import {
  BackLink,
  LeadDetailContent,
} from '@/dashboard/leads/Lead360CanonicalView';
import { workspaceLeadDetailHref } from '@/dashboard/leads/leads-workspace-view';
import { formatLeadRef } from '@/dashboard/owner/leads-object-view';
import type { Lead360Input } from '@/dashboard/owner/lead-360-view';

/** Default page size for the per-clinic lead-360 read (backend hard cap is 200). */
const LEADS_LIMIT = 100;

/**
 * Legacy owner lead detail -- the flag-OFF kill-switch render (byte-identical to
 * the pre-dedup page). Self-fetching + fault-isolated: reads `:safeLeadId` from
 * the route and `clinicId` from the query string (carried by the list row link).
 * With a `clinicId` it reads that clinic directly; without one it falls back to
 * the first owner clinic. Consumes the existing `GET /api/owner/lead-360`
 * endpoint -- no new backend route, no PHI. Composes the CANONICAL
 * `LeadDetailContent`.
 */
function OwnerLeadDetailLegacy(): JSX.Element {
  const params = useParams();
  const safeLeadId = params.safeLeadId ?? '';
  const [searchParams] = useSearchParams();
  const clinicIdParam = searchParams.get('clinicId');
  const meCtx = useDashboardMeContext();
  // Server-computed signal (flag + role); in this legacy path it is absent/false.
  const polished = meCtx?.me?.leadsWorkspaceEnabled === true;
  // S2-P2C: workbench visibility (role + /me flag). Fail-closed when absent.
  const workbenchEnabled = canUseAppointmentWorkbench(
    meCtx?.me?.role,
    meCtx?.me?.crmAppointmentWorkbenchEnabled,
  );

  const [records, setRecords] = useState<Lead360Input[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bumped on a successful appointment mutation to re-fetch the lead (refresh
  // the appointment section + history).
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    async function run(): Promise<void> {
      let clinicId = clinicIdParam ?? '';
      if (clinicId === '') {
        const { clinics } = await fetchOwnerDashboard();
        clinicId = clinics.find((c) => c.clinicId.trim() !== '')?.clinicId ?? '';
      }
      if (clinicId === '') {
        if (!cancelled) setRecords([]);
        return;
      }
      const rows = await fetchOwnerLead360(clinicId, LEADS_LIMIT);
      if (!cancelled) setRecords(rows);
    }

    run()
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setRecords(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clinicIdParam, reloadKey]);

  if (error !== null) {
    return (
      <section className="lead-detail">
        <div className="page-header">
          <p className="lead-detail__back">
            <BackLink />
          </p>
          <h1>{formatLeadRef(safeLeadId)}</h1>
        </div>
        <div className="mb-4">
          <DashboardLoadError message={error} />
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="lead-detail">
        <div className="page-header">
          <p className="lead-detail__back">
            <BackLink />
          </p>
          <h1>{formatLeadRef(safeLeadId)}</h1>
        </div>
        <div className="loading">Loading…</div>
      </section>
    );
  }

  return (
    <>
      {/*
        FRONT-DESK-POSSIBLE-MATCH-SURFACE-01: advisory "possible existing
        patient" notice for the lead in view. Self-gated by the server
        `me.patientMasterEnabled` signal (renders nothing AND never fetches
        when the flag is off). Display-only — never links / merges / creates.
      */}
      <LeadPatientCrossmatchNotice safeLeadId={safeLeadId} />
      <LeadDetailContent
        records={records ?? []}
        safeLeadId={safeLeadId}
        polished={polished}
        renderAppointmentControl={
          workbenchEnabled
            ? // CHANNEL-TO-BOOKED-APPOINTMENT-V1: the SAME booking door as the
              // canonical Leads Workspace detail -- the branch step travels
              // with the workbench, so the two surfaces can never diverge
              // into a second booking pattern.
              (record): ReactNode => (
                <AppointmentWorkbench
                  leadId={Number(record.safeLeadId)}
                  section={record.appointmentSection}
                  bookConfirmOnly={isBookConfirmOnlyRole(meCtx?.me?.role)}
                  allowConfirmation={meCtx?.me?.role !== 'receptionist'}
                  book={bookAppointment}
                  leadClinicId={record.clinicId}
                  clinicOptions={meCtx?.me?.clinicOptions ?? []}
                  originLabel={resolveOriginChipLabel({
                    sourceNormalized: record.sourceNormalized,
                    utmSource: record.utmSource,
                    utmCampaign: record.utmCampaign,
                  })}
                  setBookingClinic={setLeadBookingClinic}
                  confirm={confirmAppointment}
                  reschedule={rescheduleAppointment}
                  cancel={cancelAppointment}
                  cancelReasonCodes={resolveCancelReasonCodes(meCtx?.me ?? null)}
                  noShow={markAppointmentNoShow}
                  arrived={markAppointmentArrived}
                  checkedIn={markAppointmentCheckedIn}
                  fulfilled={markAppointmentFulfilled}
                  notConverted={markAppointmentNotConverted}
                  onChanged={(): void => setReloadKey((k) => k + 1)}
                />
              )
            : undefined
        }
      />
    </>
  );
}

/**
 * Pure redirect decision for the owner detail route (unit-testable without jsdom,
 * mirroring `decideLeadsWorkspaceAccess`):
 *   - `wait`     : `me` still loading -> render a loading shell (do not fetch).
 *   - `redirect` : the canonical Leads Workspace surface is live for this owner
 *                  -> go to the ONE canonical detail route, preserving clinicId.
 *   - `legacy`   : kill-switch OFF (or no `me`) -> render the legacy owner detail
 *                  (byte-identical to the pre-dedup page).
 * Presentation only; the backend route auth on both endpoints stays the boundary.
 */
export type OwnerLeadDetailDecision =
  | { kind: 'wait' }
  | { kind: 'legacy' }
  | { kind: 'redirect'; to: string };

export function decideOwnerLeadDetail(state: {
  loading: boolean;
  leadsWorkspaceEnabled?: boolean;
  safeLeadId: string;
  clinicId: string | null;
}): OwnerLeadDetailDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.leadsWorkspaceEnabled === true) {
    return {
      kind: 'redirect',
      to: workspaceLeadDetailHref(state.safeLeadId, state.clinicId),
    };
  }
  return { kind: 'legacy' };
}

/**
 * Flag-aware shim. Route-singularity when the canonical surface is live; the
 * legacy owner detail as the kill-switch when it is not. Both branches compose
 * the ONE canonical `LeadDetailContent` (Lead360CanonicalView), so there is a
 * single lead-360 component regardless of which route mounted it.
 */
export default function LeadDetailPage(): JSX.Element {
  const params = useParams();
  const safeLeadId = params.safeLeadId ?? '';
  const [searchParams] = useSearchParams();
  const clinicIdParam = searchParams.get('clinicId');
  const meCtx = useDashboardMeContext();

  const decision = decideOwnerLeadDetail({
    loading: meCtx?.loading === true,
    leadsWorkspaceEnabled: meCtx?.me?.leadsWorkspaceEnabled,
    safeLeadId,
    clinicId: clinicIdParam,
  });

  switch (decision.kind) {
    case 'wait':
      // Brief loading shell rather than briefly rendering (and fetching) the
      // legacy path only to redirect a moment later.
      return (
        <section className="lead-detail">
          <div className="page-header">
            <p className="lead-detail__back">
              <BackLink />
            </p>
            <h1>{formatLeadRef(safeLeadId)}</h1>
          </div>
          <div className="loading">Loading…</div>
        </section>
      );
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'legacy':
      return <OwnerLeadDetailLegacy />;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return <OwnerLeadDetailLegacy />;
    }
  }
}
